<template>
  <div
    class="chat-page"
    :class="{ 'chat-page--mobile-conv': isMobile && panelConversation }"
  >
    <!-- Left: conversation list -->
    <div class="chat-left border-r border-gray-200">
      <!-- Tabs -->
      <div class="chat-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="chat-tab"
          :class="{ 'chat-tab--active': activeTab === tab.key }"
          @click="switchTab(tab.key)"
        >
          {{ tab.label }}
          <span v-if="tab.key === 'queue'" class="badge badge--red">{{
            queue.length
          }}</span>
          <span v-if="tab.key === 'mine'" class="badge">{{
            myConversations.length
          }}</span>
        </button>
      </div>

      <!-- Queue tab -->
      <div v-if="activeTab === 'queue'" class="chat-list">
        <div v-if="queue.length === 0" class="empty-state">
          No conversations waiting
        </div>
        <div
          v-for="conv in queue"
          :key="conv.conversationId"
          class="conv-card conv-card--clickable"
          :class="{
            'conv-card--selected':
              panelConversation && panelConversation.id === conv.conversationId
          }"
          @click="previewQueueConversation(conv)"
        >
          <div class="conv-card__info">
            <span class="conv-card__email">{{
              conv.clientEmail || "Anonymous"
            }}</span>
            <span class="conv-card__time">{{
              formatTime(conv.createdAt)
            }}</span>
          </div>
        </div>
      </div>

      <!-- Mine tab -->
      <div v-if="activeTab === 'mine'" class="chat-list">
        <div v-if="myConversations.length === 0" class="empty-state">
          No active chats
        </div>
        <div
          v-for="conv in myConversations"
          :key="conv.id"
          class="conv-card conv-card--clickable"
          :class="{
            'conv-card--selected': selectedConversationId === conv.id,
            'conv-card--closed': conv.status === 'closed'
          }"
          @click="selectConversation(conv)"
        >
          <div class="conv-card__info">
            <span class="conv-card__email">{{
              conv.clientEmail || conv.client_email || "Chat #" + conv.id
            }}</span>
            <span class="conv-card__status" :class="'status--' + conv.status">
              {{ conv.status }}
            </span>
          </div>
        </div>
      </div>

      <!-- All tab -->
      <div v-if="activeTab === 'all'" class="chat-list">
        <!-- Filters -->
        <div class="all-filters">
          <select v-model="allFilters.status" class="filter-select">
            <option value="">All statuses</option>
            <option value="waiting">Waiting</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
          </select>
          <input
            v-model="allFilters.agentId"
            type="number"
            placeholder="Agent ID"
            class="filter-input"
          />
          <input v-model="allFilters.from" type="date" class="filter-input" />
          <input v-model="allFilters.to" type="date" class="filter-input" />
          <div class="filter-actions">
            <button class="btn-search" @click="loadAll">
              <i class="mdi mdi-magnify" />
            </button>
            <button class="btn-clear" @click="clearAllFilters">
              <i class="mdi mdi-close" />
            </button>
          </div>
        </div>

        <div v-if="allLoading" class="empty-state">
          <i class="mdi mdi-loading mdi-spin" /> Loading...
        </div>
        <div v-else-if="allConversations.length === 0" class="empty-state">
          No conversations found
        </div>
        <div
          v-for="conv in allConversations"
          :key="conv.id"
          class="conv-card conv-card--clickable"
          :class="{ 'conv-card--selected': selectedConversationId === conv.id }"
          @click="selectConversation(conv)"
        >
          <div class="conv-card__info">
            <span class="conv-card__email">{{
              conv.clientEmail || conv.client_email || "Chat #" + conv.id
            }}</span>
            <div class="conv-card__meta">
              <span
                class="status-badge"
                :class="'status-badge--' + conv.status"
                >{{ conv.status }}</span
              >
              <span
                v-if="conv.agent_name || conv.agent_id || conv.agentId"
                class="conv-card__agent"
                :class="{
                  'conv-card__agent--me':
                    (conv.agent_id || conv.agentId) === myAgentId
                }"
              >
                <i class="mdi mdi-account" />
                {{
                  (conv.agent_id || conv.agentId) === myAgentId
                    ? "You"
                    : conv.agent_name ||
                      "Agent #" + (conv.agent_id || conv.agentId)
                }}
              </span>
              <span v-else class="status-badge status-badge--unassigned">
                unassigned
              </span>
            </div>
            <span class="conv-card__time">{{
              formatTime(conv.created_at || conv.createdAt)
            }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Right: message panel -->
    <div class="chat-right flex flex-col">
      <template v-if="panelConversation">
        <!-- Header -->
        <div class="chat-column__header">
          <button v-if="isMobile" class="btn-back" @click="goBackToList">
            <i class="mdi mdi-arrow-left" />
          </button>
          <div class="flex flex-col" style="min-width: 0; flex: 1">
            <span class="font-semibold">
              {{
                panelConversation.clientEmail ||
                  panelConversation.client_email ||
                  "Chat #" + panelConversation.id
              }}
            </span>
            <div class="header-meta">
              <span class="text-xs text-gray-400"
                >#{{ panelConversation.id }}</span
              >
              <span
                :class="
                  'status-badge status-badge--' + panelConversation.status
                "
                >{{ panelConversation.status }}</span
              >
              <span
                v-if="panelConversation.agent_id || panelConversation.agentId"
                class="header-agent-badge"
                :class="{ 'header-agent-badge--me': panelAgentName === 'You' }"
              >
                <i class="mdi mdi-account" />
                {{ panelAgentName }}
              </span>
              <span v-else class="status-badge status-badge--unassigned">
                unassigned
              </span>
            </div>
          </div>

          <div class="header-actions">
            <!-- Transfer dropdown -->
            <div
              v-if="panelConversation.status === 'active'"
              class="transfer-wrapper"
            >
              <button class="btn-transfer" @click="toggleTransferDropdown">
                <i class="mdi mdi-account-switch" /> Transfer
              </button>
              <div v-if="showTransferDropdown" class="transfer-dropdown">
                <div class="transfer-dropdown__title">Transfer to:</div>
                <div v-if="agentsLoading" class="transfer-dropdown__loading">
                  <i class="mdi mdi-loading mdi-spin" />
                </div>
                <div
                  v-for="agent in availableAgents"
                  :key="agent.id"
                  class="transfer-dropdown__item"
                  :class="{
                    'transfer-dropdown__item--me': agent.id === myAgentId
                  }"
                  @click="transferTo(agent)"
                >
                  <i class="mdi mdi-account" />
                  {{
                    agent.id === myAgentId ? agent.name + " (You)" : agent.name
                  }}
                </div>
              </div>
            </div>

            <div
              v-if="panelConversation.status === 'waiting'"
              class="flex gap-2"
            >
              <button
                class="btn-take"
                @click="takeConversation(panelConversation.id)"
              >
                Take
              </button>
              <button
                class="btn-close-conv"
                @click="closeConv(panelConversation.id)"
              >
                Close
              </button>
            </div>
            <button
              v-if="
                panelConversation.status === 'active' &&
                  isMyConversation(panelConversation.id)
              "
              class="btn-close-conv"
              @click="closeConv(panelConversation.id)"
            >
              Close chat
            </button>
          </div>
        </div>

        <!-- Messages -->
        <div ref="messageList" class="chat-messages flex-1 overflow-y-auto p-4">
          <div v-if="panelLoadingMessages" class="empty-state">
            <i class="mdi mdi-loading mdi-spin" /> Loading messages...
          </div>
          <template v-else>
            <div
              v-for="(msg, i) in panelMessages"
              :key="i"
              class="chat-message"
              :class="
                (msg.senderType || msg.sender_type) === 'agent'
                  ? 'chat-message--agent'
                  : 'chat-message--client'
              "
            >
              <span
                class="chat-message__sender"
                v-if="(msg.senderType || msg.sender_type) === 'agent'"
              >
                {{ messageSenderName(msg) }}
              </span>
              <img
                v-if="(msg.messageType || msg.message_type) === 'image'"
                :src="msg.body"
                class="chat-image"
                alt="Shared image"
                @click="previewImage = msg.body"
              />
              <span v-else>{{ msg.body }}</span>
              <time>{{ formatTime(msg.createdAt || msg.created_at) }}</time>
            </div>
            <div v-if="panelMessages.length === 0" class="empty-state">
              No messages yet
            </div>
          </template>
        </div>

        <!-- Image preview before send -->
        <div
          v-if="pendingImage"
          class="chat-image-preview border-t border-gray-200"
        >
          <img
            :src="pendingImageUrl"
            alt="Preview"
            class="chat-image-preview__img"
          />
          <div class="chat-image-preview__actions">
            <button class="chat-image-preview__cancel" @click="cancelImage">
              Cancel
            </button>
            <button
              class="chat-image-preview__send"
              :disabled="uploading"
              @click="confirmSendImage"
            >
              {{ uploading ? "Sending..." : "Send" }}
            </button>
          </div>
        </div>

        <!-- Input (for my active conversations, or any if non-superadmin) -->
        <div
          v-else-if="
            panelConversation.status === 'active' &&
              (isMyConversation(panelConversation.id) || canManageAnyChat)
          "
          class="chat-input border-t border-gray-200 p-3"
        >
          <button
            class="chat-input__icon"
            title="Send image"
            @click="$refs.adminFileInput.click()"
          >
            <i class="mdi mdi-image-outline" />
          </button>
          <input
            ref="adminFileInput"
            type="file"
            accept="image/*"
            style="display:none"
            @change="handleAdminFileSelect"
          />
          <input
            v-model="inputText"
            class="chat-input__field"
            placeholder="Type a message..."
            @keydown.enter.prevent="send"
            @paste="handleAdminPaste"
          />
          <button
            class="chat-input__send"
            :disabled="!inputText.trim()"
            @click="send"
          >
            Send
          </button>
        </div>
        <div
          v-else-if="panelConversation.status === 'closed'"
          class="p-3 text-center text-gray-400 text-sm border-t border-gray-200"
        >
          Conversation closed
        </div>
        <div
          v-else-if="!isMyConversation(panelConversation.id)"
          class="p-3 text-center text-gray-400 text-sm border-t border-gray-200"
        >
          Read-only — not assigned to you
        </div>
      </template>

      <div
        v-else
        class="flex-1 flex items-center justify-center text-gray-400 text-sm"
      >
        Select a conversation
      </div>
    </div>

    <!-- Image preview overlay -->
    <div
      v-if="previewImage"
      class="chat-image-overlay"
      @click="previewImage = null"
    >
      <img :src="previewImage" alt="Preview" />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import {
  joinConversation,
  watchConversation,
  sendAgentMessage,
  sendAgentImage,
  closeConversation
} from "@/plugins/chat";

const CHAT_API = () =>
  process.env.VUE_APP_CHAT_API || "https://api-n.nkrdieselparts.com";

export default {
  name: "ChatPage",

  data() {
    return {
      activeTab: "queue",
      tabs: [
        { key: "queue", label: "Queue" },
        { key: "mine", label: "Mine" },
        { key: "all", label: "All" }
      ],
      inputText: "",
      // All tab
      allConversations: [],
      allLoading: false,
      allFilters: { status: "", agentId: "", from: "", to: "" },
      // Panel state (used for "all" tab — store-based for mine/queue)
      panelConversation: null,
      panelMessages: [],
      panelLoadingMessages: false,
      // Transfer
      showTransferDropdown: false,
      availableAgents: [],
      agentsLoading: false,
      agentsMap: {},
      previewImage: null,
      uploading: false,
      pendingImage: null,
      pendingImageUrl: null,
      windowWidth: window.innerWidth
    };
  },

  computed: {
    ...mapState("chat", ["queue", "myConversations", "selectedConversationId"]),
    ...mapGetters("chat", ["selectedConversation", "selectedMessages"]),
    myAgentId() {
      return this.agentIdFromToken();
    },
    isMobile() {
      return this.windowWidth < 768;
    },
    isSuperAdmin() {
      return this.$store.getters["auth/currentUser"]?.role === "superadmin";
    },
    canManageAnyChat() {
      // Non-superadmin can take/reassign any chat
      return !this.isSuperAdmin;
    },
    panelAgentName() {
      if (!this.panelConversation) return "Agent";
      const agentId =
        this.panelConversation.agent_id || this.panelConversation.agentId;
      if (agentId === this.myAgentId) return "You";
      return this.panelConversation.agent_name || "Agent";
    }
  },

  watch: {
    selectedConversationId(id) {
      if (!id) return;
      const conv = this.myConversations.find(c => c.id === id);
      if (conv) {
        this.activeTab = "mine";
        this.panelConversation = conv;
        this.panelMessages = this.selectedMessages;
        this.$nextTick(() => this.scrollToBottom());
      }
      this.syncQueryString(id);
    },
    selectedMessages(msgs) {
      if (this.activeTab === "mine") {
        this.panelMessages = msgs;
        this.$nextTick(() => this.scrollToBottom());
      }
    },
    panelMessages() {
      this.$nextTick(() => this.scrollToBottom());
    },
    panelConversation(conv) {
      if (conv && conv.id) {
        this.syncQueryString(conv.id);
      } else {
        this.syncQueryString(null);
      }
    }
  },

  mounted() {
    this.requestNotificationPermission();
    this.loadQueue();
    this.loadMyConversations();
    document.addEventListener("click", this.onDocClick);
    window.addEventListener("resize", this.onResize);

    // Handle pre-selected conversation from query string or store
    const queryId = Number(this.$route.query.id);
    const preId = queryId || this.selectedConversationId;
    if (preId) {
      this.openConversationById(preId);
    }
  },

  beforeDestroy() {
    document.removeEventListener("click", this.onDocClick);
    window.removeEventListener("resize", this.onResize);
  },

  methods: {
    onResize() {
      this.windowWidth = window.innerWidth;
    },

    syncQueryString(id) {
      const current = this.$route.query.id;
      const target = id ? String(id) : undefined;
      if (current === target) return;
      const query = { ...this.$route.query };
      if (target) {
        query.id = target;
      } else {
        delete query.id;
      }
      this.$router.replace({ query }).catch(() => {});
    },

    goBackToList() {
      this.panelConversation = null;
      this.panelMessages = [];
      this.$store.commit("chat/SELECT_CONVERSATION", null);
    },

    openConversationById(id) {
      const conv = this.myConversations.find(c => c.id === id);
      if (conv) {
        this.activeTab = "mine";
        this.selectConversation(conv);
        return;
      }
      const queueConv = this.queue.find(c => c.conversationId === id);
      if (queueConv) {
        this.activeTab = "queue";
        this.previewQueueConversation(queueConv);
        return;
      }
      // Not in store yet — load from API and show in panel
      this.loadAndShowConversation(id);
    },

    async loadAndShowConversation(id) {
      try {
        const res = await this.$http.get(
          `${CHAT_API()}/chat/conversations/${id}`
        );
        const conv = res.data;
        this.panelConversation = conv;
        watchConversation(conv.id);
        this.loadPanelMessages(conv.id);
        this.loadAgents();
      } catch (e) {
        console.error("Failed to load conversation", id, e);
      }
    },

    switchTab(tab) {
      this.activeTab = tab;
      if (tab === "all" && this.allConversations.length === 0) {
        this.loadAll();
      }
      // Clear panel when switching tabs
      this.panelConversation = null;
      this.panelMessages = [];
      this.$store.commit("chat/SELECT_CONVERSATION", null);
    },

    // ── Queue / Mine ────────────────────────────────────────────────────────

    async loadQueue() {
      try {
        const res = await this.$http.get(
          `${CHAT_API()}/chat/conversations?status=waiting`
        );
        res.data.forEach(conv => {
          this.$store.commit("chat/ADD_TO_QUEUE", {
            conversationId: conv.id,
            clientEmail: conv.clientEmail || conv.client_email,
            createdAt: conv.createdAt || conv.created_at
          });
        });
      } catch (e) {
        console.error("Failed to load queue", e);
      }
    },

    async loadMyConversations() {
      const agentId = this.agentIdFromToken();
      if (!agentId) return;
      try {
        const res = await this.$http.get(
          `${CHAT_API()}/chat/conversations?status=active&agentId=${agentId}`
        );
        this.$store.commit("chat/SET_MY_CONVERSATIONS", res.data);
        // Rejoin all active conversation rooms after socket reconnect
        res.data.forEach(conv => watchConversation(conv.id));
      } catch (e) {
        console.error("Failed to load my conversations", e);
      }
    },

    previewQueueConversation(conv) {
      this.panelConversation = {
        id: conv.conversationId,
        clientEmail: conv.clientEmail,
        createdAt: conv.createdAt,
        status: "waiting"
      };
      watchConversation(conv.conversationId);
      this.loadPanelMessages(conv.conversationId);
      this.loadAgents();
    },

    takeConversation(conversationId) {
      console.log("Taking conversation", conversationId);
      joinConversation(conversationId);
      // panel will be updated by the selectedConversationId watcher
      // when chat:join_success fires from the backend
    },

    selectConversation(conv) {
      this.panelConversation = conv;
      watchConversation(conv.id);
      this.loadAgents();
      if (this.activeTab === "mine") {
        this.$store.commit("chat/SELECT_CONVERSATION", conv.id);
        if (!this.$store.state.chat.messages[conv.id]) {
          this.$store.dispatch("chat/loadMessages", conv.id);
        }
        this.panelMessages = this.selectedMessages;
      } else {
        this.loadPanelMessages(conv.id);
      }
    },

    async loadPanelMessages(conversationId) {
      this.panelLoadingMessages = true;
      this.panelMessages = [];
      try {
        const res = await this.$http.get(
          `${CHAT_API()}/chat/conversations/${conversationId}/messages`
        );
        this.panelMessages = res.data;
      } catch (e) {
        console.error("Failed to load messages", e);
      } finally {
        this.panelLoadingMessages = false;
      }
    },

    send() {
      const body = this.inputText.trim();
      if (!body || !this.panelConversation) return;
      const convId = this.panelConversation.id;
      sendAgentMessage(convId, body);
      this.$store.commit("chat/ADD_MESSAGE", {
        conversationId: convId,
        message: {
          senderType: "agent",
          body,
          createdAt: new Date().toISOString()
        }
      });
      this.inputText = "";
    },

    closeConv(conversationId) {
      if (confirm("Close this conversation?")) {
        closeConversation(conversationId);
        this.$store.commit("chat/REMOVE_FROM_QUEUE", conversationId);
        this.panelConversation = null;
        this.panelMessages = [];
      }
    },

    isMyConversation(id) {
      return !!this.myConversations.find(c => c.id === id);
    },

    // ── All tab ─────────────────────────────────────────────────────────────

    async loadAll() {
      this.allLoading = true;
      try {
        const params = new URLSearchParams();
        if (this.allFilters.status)
          params.append("status", this.allFilters.status);
        if (this.allFilters.agentId)
          params.append("agentId", this.allFilters.agentId);
        if (this.allFilters.from) params.append("from", this.allFilters.from);
        if (this.allFilters.to) params.append("to", this.allFilters.to);
        const res = await this.$http.get(
          `${CHAT_API()}/chat/conversations?${params.toString()}`
        );
        this.allConversations = res.data;
      } catch (e) {
        console.error("Failed to load all conversations", e);
      } finally {
        this.allLoading = false;
      }
    },

    clearAllFilters() {
      this.allFilters = { status: "", agentId: "", from: "", to: "" };
      this.loadAll();
    },

    // ── Utils ────────────────────────────────────────────────────────────────

    onDocClick(e) {
      if (!e.target.closest(".transfer-wrapper")) {
        this.showTransferDropdown = false;
      }
    },

    async loadAgents() {
      if (this.availableAgents.length > 0) return;
      this.agentsLoading = true;
      try {
        const res = await this.$http.get(`${CHAT_API()}/chat/agents`);
        this.availableAgents = res.data || res;
        this.agentsMap = {};
        this.availableAgents.forEach(a => {
          this.agentsMap[a.id] = a.name;
        });
      } catch (e) {
        console.error("Failed to load agents", e);
      } finally {
        this.agentsLoading = false;
      }
    },

    async toggleTransferDropdown() {
      this.showTransferDropdown = !this.showTransferDropdown;
      if (this.showTransferDropdown) {
        await this.loadAgents();
      }
    },

    messageSenderName(msg) {
      const senderId = msg.sender_id || msg.senderId;
      if (senderId === this.myAgentId) return "You";
      if (senderId && this.agentsMap[senderId]) return this.agentsMap[senderId];
      return "Agent";
    },

    async transferTo(agent) {
      this.showTransferDropdown = false;
      try {
        await this.$http.put(
          `${CHAT_API()}/chat/conversations/${
            this.panelConversation.id
          }/transfer`,
          { agentId: agent.id }
        );
        // Update local panel state
        this.$set(this.panelConversation, "agent_id", agent.id);
        this.$set(this.panelConversation, "agent_name", agent.name);
        // Also update myConversations in store if present
        const conv = this.myConversations.find(
          c => c.id === this.panelConversation.id
        );
        if (conv) {
          this.$set(conv, "agent_id", agent.id);
          this.$set(conv, "agent_name", agent.name);
        }
        const label = agent.id === this.myAgentId ? "yourself" : agent.name;
        this.$toast.success(`Chat transferred to ${label}`, {
          position: "top-right",
          duration: 3000
        });
      } catch (e) {
        console.error("Failed to transfer conversation", e);
        this.$toast.error("Failed to transfer chat", {
          position: "top-right",
          duration: 3000
        });
      }
    },

    agentIdFromToken() {
      try {
        const token =
          this.$store.state.auth.currentUser &&
          this.$store.state.auth.currentUser.token;
        if (!token) return null;
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload.id || null;
      } catch (e) {
        return null;
      }
    },

    scrollToBottom() {
      const el = this.$refs.messageList;
      if (el) el.scrollTop = el.scrollHeight;
    },

    formatTime(iso) {
      if (!iso) return "";
      const date = new Date(
        typeof iso === "string" ? iso.replace(" ", "T") : iso
      );
      if (isNaN(date.getTime())) return "";

      const now = new Date();
      const isToday =
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear();
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      const isYesterday =
        date.getDate() === yesterday.getDate() &&
        date.getMonth() === yesterday.getMonth() &&
        date.getFullYear() === yesterday.getFullYear();

      const time = date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit"
      });

      if (isToday) return `Today at ${time}`;
      if (isYesterday) return `Yesterday at ${time}`;
      return (
        date.toLocaleDateString([], { month: "short", day: "numeric" }) +
        ` at ${time}`
      );
    },

    handleAdminFileSelect(e) {
      const file = e.target.files[0];
      if (file) this.showImagePreview(file);
      e.target.value = "";
    },

    handleAdminPaste(e) {
      const items = e.clipboardData && e.clipboardData.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) this.showImagePreview(file);
          return;
        }
      }
    },

    showImagePreview(file) {
      this.pendingImage = file;
      this.pendingImageUrl = URL.createObjectURL(file);
    },

    cancelImage() {
      if (this.pendingImageUrl) URL.revokeObjectURL(this.pendingImageUrl);
      this.pendingImage = null;
      this.pendingImageUrl = null;
    },

    async confirmSendImage() {
      if (!this.pendingImage || !this.panelConversation) return;
      await this.uploadAndSendAdmin(this.pendingImage);
      this.cancelImage();
    },

    async uploadAndSendAdmin(file) {
      if (!this.panelConversation) return;
      this.uploading = true;
      try {
        const chatApi = CHAT_API();
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch(`${chatApi}/chat/upload`, {
          method: "POST",
          body: formData
        });
        if (!res.ok) throw new Error("Upload failed");
        const data = await res.json();
        const convId = this.panelConversation.id;
        sendAgentImage(convId, data.url);
        this.$store.commit("chat/ADD_MESSAGE", {
          conversationId: convId,
          message: {
            senderType: "agent",
            body: data.url,
            messageType: "image",
            createdAt: new Date().toISOString()
          }
        });
      } catch (err) {
        this.$toast.error("Failed to upload image", {
          position: "top-right",
          duration: 3000
        });
      } finally {
        this.uploading = false;
      }
    },

    requestNotificationPermission() {
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission();
      }
    }
  }
};
</script>

<style scoped>
.chat-page {
  display: flex;
  background: #f9fafb;
  /* 100vh incluye las barras del navegador en iOS y deja el pie del chat
     debajo de ellas; dvh usa el alto realmente visible. */
  height: calc(100vh - 64px);
  height: calc(100dvh - 64px);
}

/* Left panel */
.chat-left {
  width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: white;
  overflow: hidden;
}

.chat-tabs {
  display: flex;
  border-bottom: 1px solid #e5e7eb;
}
.chat-tab {
  flex: 1;
  padding: 10px 4px;
  font-size: 12px;
  font-weight: 600;
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-bottom: 2px solid transparent;
  transition: color 0.15s, border-color 0.15s;
}
.chat-tab--active {
  color: #1a56db;
  border-bottom-color: #1a56db;
}

.badge {
  background: #e5e7eb;
  color: #374151;
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 9999px;
  font-weight: 700;
}
.badge--red {
  background: #fee2e2;
  color: #dc2626;
}

.chat-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

/* All filters */
.all-filters {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
  margin-bottom: 8px;
}
.filter-select,
.filter-input {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  padding: 5px 8px;
  font-size: 12px;
  outline: none;
  background: white;
}
.filter-select:focus,
.filter-input:focus {
  border-color: #1a56db;
}
.filter-actions {
  display: flex;
  gap: 6px;
}
.btn-search,
.btn-clear {
  flex: 1;
  border: none;
  border-radius: 5px;
  padding: 5px;
  font-size: 14px;
  cursor: pointer;
}
.btn-search {
  background: #1a56db;
  color: white;
}
.btn-clear {
  background: #f3f4f6;
  color: #6b7280;
}

/* Conv cards */
.empty-state {
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
  padding: 24px 8px;
}
.conv-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 6px;
}
.conv-card--clickable {
  cursor: pointer;
  transition: border-color 0.15s;
}
.conv-card--clickable:hover {
  border-color: #93c5fd;
}
.conv-card--selected {
  border-color: #1a56db;
  background: #eff6ff;
}
.conv-card--closed {
  opacity: 0.6;
}

.conv-card__info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 6px;
}
.conv-card__email {
  font-size: 12px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.conv-card__meta {
  display: flex;
  align-items: center;
  gap: 6px;
}
.conv-card__time,
.conv-card__status,
.conv-card__agent {
  font-size: 11px;
  color: #6b7280;
}
.conv-card__agent--me {
  color: #1a56db;
  font-weight: 600;
}
.status--active {
  color: #059669;
}
.status--closed {
  color: #9ca3af;
}

.status-badge {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 700;
  text-transform: capitalize;
}
.status-badge--waiting {
  background: #fef3c7;
  color: #92400e;
}
.status-badge--active {
  background: #d1fae5;
  color: #065f46;
}
.status-badge--closed {
  background: #f3f4f6;
  color: #6b7280;
}
.status-badge--unassigned {
  background: #fee2e2;
  color: #dc2626;
}

.btn-take {
  width: 52px;
  background: #1a56db;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 5px 0;
  font-size: 12px;
  cursor: pointer;
}
.btn-take:hover {
  background: #1e40af;
}

/* Right panel */
.chat-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-column__header {
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  gap: 12px;
}

.header-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 2px;
}

.header-agent-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  padding: 1px 7px;
  border-radius: 9999px;
  background: #f3f4f6;
  color: #374151;
  font-weight: 500;

  &--me {
    background: #dbeafe;
    color: #1d4ed8;
    font-weight: 700;
  }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.transfer-wrapper {
  position: relative;
}

.btn-transfer {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  color: #374151;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;

  &:hover {
    background: #e5e7eb;
  }
}

.transfer-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-width: 200px;
  z-index: 100;
  overflow: hidden;

  &__title {
    padding: 8px 12px 4px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #9ca3af;
  }

  &__loading {
    padding: 12px;
    text-align: center;
    color: #9ca3af;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 12px;
    font-size: 13px;
    color: #111827;
    cursor: pointer;
    transition: background 0.1s;

    &:hover {
      background: #f5f5f5;
    }

    &--me {
      color: #1d4ed8;
      font-weight: 600;
    }
  }
}

.btn-close-conv {
  background: transparent;
  border: 1px solid #fca5a5;
  color: #ef4444;
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
}

.chat-messages {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chat-message {
  max-width: 70%;
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.chat-message time {
  font-size: 10px;
  opacity: 0.6;
}
.chat-message__sender {
  font-size: 10px;
  font-weight: 600;
  opacity: 0.7;
  margin-bottom: 2px;
}
.chat-message--agent {
  background: #1a56db;
  color: white;
  align-self: flex-end;
  border-bottom-right-radius: 2px;
}
.chat-message--client {
  background: #f3f4f6;
  color: #111827;
  align-self: flex-start;
  border-bottom-left-radius: 2px;
}

.chat-input {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}
.chat-input__field {
  flex: 1;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  outline: none;
}
.chat-input__field:focus {
  border-color: #1a56db;
}
.chat-input__send {
  background: #1a56db;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 18px;
  font-size: 14px;
  cursor: pointer;
}
.chat-input__send:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.chat-input__icon {
  background: transparent;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  font-size: 20px;
  padding: 4px;
  display: flex;
  align-items: center;
  transition: color 0.15s;
}
.chat-input__icon:hover {
  color: #1a56db;
}

.chat-image {
  max-width: 220px;
  max-height: 180px;
  border-radius: 8px;
  cursor: pointer;
  object-fit: cover;
}
.chat-message--agent .chat-image {
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Image preview before send */
.chat-image-preview {
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  background: #f9fafb;
}
.chat-image-preview__img {
  max-width: 100%;
  max-height: 200px;
  border-radius: 8px;
  object-fit: contain;
}
.chat-image-preview__actions {
  display: flex;
  gap: 8px;
  width: 100%;
  max-width: 300px;
}
.chat-image-preview__cancel {
  flex: 1;
  padding: 7px;
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
}
.chat-image-preview__cancel:hover {
  background: #e5e7eb;
}
.chat-image-preview__send {
  flex: 1;
  padding: 7px;
  background: #1a56db;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
}
.chat-image-preview__send:hover {
  background: #1e40af;
}
.chat-image-preview__send:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.chat-upload-bar {
  padding: 6px 16px;
  font-size: 12px;
  color: #6b7280;
  border-top: 1px solid #f3f4f6;
}

.chat-image-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  cursor: pointer;
}
.chat-image-overlay img {
  max-width: 90vw;
  max-height: 90vh;
  border-radius: 8px;
  object-fit: contain;
}

/* Back button for mobile */
.btn-back {
  background: none;
  border: none;
  color: #1a56db;
  font-size: 20px;
  cursor: pointer;
  padding: 4px 8px 4px 0;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

/* Mobile layout */
@media (max-width: 767px) {
  .chat-page {
    flex-direction: column;
  }
  .chat-left {
    width: 100%;
    flex-shrink: 1;
    flex: 1;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
  }
  .chat-right {
    display: none;
  }
  /* When a conversation is selected on mobile, hide list and show conv */
  .chat-page--mobile-conv .chat-left {
    display: none;
  }
  .chat-page--mobile-conv .chat-right {
    display: flex;
    flex: 1;
  }
}
</style>
